import { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { addNotification } from 'modules/notifications/redux';
import { NotificationTypes } from 'modules/notifications';
import { ValidationStatus } from 'modules/ui/components/DragAndDrop';
import { SettingsDefaults } from 'modules/settings/types';
import { BoatsApi } from '../api';
import { BoatsActions, BoatsSelectors } from '../redux';

type FileValidationState = {
  status: ValidationStatus;
  error?: string;
};

export const useCreateOrUpdateBoat = (closeModal: () => void) => {
  const dispatch = useDispatch();
  const boats = useSelector(BoatsSelectors.boatsList);
  const selectedBoat = useSelector(BoatsSelectors.selectedBoat);
  const selectedBoatId = useSelector(BoatsSelectors.selectedBoatId);
  const [isCreatingNew, setIsCreatingNew] = useState(false);
  // When creating a new boat we ignore the currently selected boat.
  const boat = isCreatingNew ? undefined : selectedBoat;
  const [polarsFile, setPolarFile] = useState<File | null>(null);
  const [sailsFile, setSailsFile] = useState<File | null>(null);
  const [waveFile, setWaveFile] = useState<File | null>(null);
  const [loading, setLoading] = useState(false);

  const [polarsValidation, setPolarsValidation] = useState<FileValidationState>(
    { status: 'idle' },
  );
  const [sailsValidation, setSailsValidation] = useState<FileValidationState>({
    status: 'idle',
  });
  const [waveValidation, setWaveValidation] = useState<FileValidationState>({
    status: 'idle',
  });

  // Flags marking an existing (already saved) file for removal on save.
  // Polar is required, so it can only be replaced, never removed.
  const [removeSails, setRemoveSails] = useState(false);
  const [removeWave, setRemoveWave] = useState(false);

  const resetFiles = useCallback(() => {
    setPolarFile(null);
    setSailsFile(null);
    setWaveFile(null);
    setPolarsValidation({ status: 'idle' });
    setSailsValidation({ status: 'idle' });
    setWaveValidation({ status: 'idle' });
    setRemoveSails(false);
    setRemoveWave(false);
  }, []);

  // Clear selected files/validations whenever the edited boat changes.
  useEffect(() => {
    resetFiles();
  }, [selectedBoatId, isCreatingNew, resetFiles]);

  const selectBoat = useCallback(
    (id: number) => {
      setIsCreatingNew(false);
      dispatch(BoatsActions.setSelectedBoatId(id));
    },
    [dispatch],
  );

  const startCreatingNew = useCallback(() => {
    setIsCreatingNew(true);
  }, []);

  const validateFile = useCallback(
    async (
      file: File,
      type: 'polars' | 'sails' | 'wave',
      setValidation: (state: FileValidationState) => void,
    ) => {
      setValidation({ status: 'validating' });
      try {
        await BoatsApi.validateBoatFiles(file, type);
        setValidation({ status: 'valid' });
      } catch (err: any) {
        const message =
          err?.response?.data?.message || 'File validation failed';
        setValidation({ status: 'error', error: message });
      }
    },
    [],
  );

  const handleSetPolarFile = useCallback(
    (file: File) => {
      setPolarFile(file);
      validateFile(file, 'polars', setPolarsValidation);
    },
    [validateFile],
  );

  const handleSetSailsFile = useCallback(
    (file: File) => {
      setRemoveSails(false);
      setSailsFile(file);
      validateFile(file, 'sails', setSailsValidation);
    },
    [validateFile],
  );

  const handleSetWaveFile = useCallback(
    (file: File) => {
      setRemoveWave(false);
      setWaveFile(file);
      validateFile(file, 'wave', setWaveValidation);
    },
    [validateFile],
  );

  // Empties the field: drops any newly picked file and marks a saved file
  // for removal on the next save.
  const removeSailsFile = useCallback(() => {
    setSailsFile(null);
    setSailsValidation({ status: 'idle' });
    setRemoveSails(true);
  }, []);

  const removeWaveFile = useCallback(() => {
    setWaveFile(null);
    setWaveValidation({ status: 'idle' });
    setRemoveWave(true);
  }, []);

  const handleUpdateOrCreate = useCallback(
    async ({ name, config }: { name: string; config?: SettingsDefaults }) => {
      setLoading(true);
      try {
        const files = {
          polars: polarsFile,
          sails: sailsFile,
          wave: waveFile,
        };

        if (boat) {
          const updatedBoat = await BoatsApi.updateBoat({
            files,
            remove: {
              sails: removeSails,
              wave: removeWave,
            },
            config,
            id: boat.id,
            name,
          });
          dispatch(
            BoatsActions.setBoats(
              boats.map((b) => (b.id === updatedBoat.id ? updatedBoat : b)),
            ),
          );
        } else {
          const createdBoat = await BoatsApi.createBoat({
            files,
            config,
            name,
          });
          dispatch(BoatsActions.setBoats([...boats, createdBoat]));
          dispatch(BoatsActions.setSelectedBoatId(createdBoat.id));
        }

        dispatch(
          addNotification({
            type: NotificationTypes.Success,
            message: `Boat has been ${
              boat ? 'updated' : 'created'
            } successfully.`,
          }),
        );
        closeModal();
      } catch (err) {
        dispatch(
          addNotification({
            type: NotificationTypes.Error,
            message: `Error while ${boat ? 'updating' : 'creating a'} boat.`,
          }),
        );
      } finally {
        setLoading(false);
      }
    },
    [
      dispatch,
      polarsFile,
      sailsFile,
      waveFile,
      removeSails,
      removeWave,
      boat,
      boats,
      closeModal,
    ],
  );

  const hasValidationErrors =
    polarsValidation.status === 'error' ||
    sailsValidation.status === 'error' ||
    waveValidation.status === 'error';

  const isValidating =
    polarsValidation.status === 'validating' ||
    sailsValidation.status === 'validating' ||
    waveValidation.status === 'validating';

  return {
    loading,
    polarsFile,
    setPolarFile: handleSetPolarFile,
    sailsFile,
    setSailsFile: handleSetSailsFile,
    removeSailsFile,
    removeSails,
    waveFile,
    setWaveFile: handleSetWaveFile,
    removeWaveFile,
    removeWave,
    setLoading,
    handleUpdateOrCreate,
    boat,
    boats,
    selectedBoatId,
    isCreatingNew,
    selectBoat,
    startCreatingNew,
    boatExists: !!boat,
    initialName: boat?.name || '',
    polarsValidation,
    sailsValidation,
    waveValidation,
    hasValidationErrors,
    isValidating,
  };
};
